import { useCallback } from 'react';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useService } from '@atlassian/help-center-common-util/use-service';
import { usePfErrorUtils } from '@atlassian/proforma-common-core/error-utils-context';

import { useServiceDeskId } from '../../controllers/request-create-context-provider';
import { proformaGasAnalyticsFetch } from '../../common/services/proforma-gas-analytics-fetch';
import { JSM_CUSTOMER_PORTAL_URL_PREFIX } from '../constants';

export interface UserPickerUser {
	accountId: string;
	displayName: string | null;
	html: string;
	avatarUrl?: string;
}

/**
 * User search service for ProForma user picker fields (without field-specific config).
 *
 * Despite the URL pattern being a JSM customer portal REST API
 * (/rest/servicedesk/1/customer/portal/{id}/...), the /user-search/proforma sub-endpoint
 * expects serviceDeskId rather than portalId. These IDs coincide on most tenants but diverge
 * on older or migrated tenants.
 *
 * See docs/PORTAL_ID_AND_SERVICE_DESK_ID.md for the distinction.
 */
export const useUsersService = () => {
	di(useServiceDeskId);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const errorUtils = usePfErrorUtils();
	const serviceDeskId = useServiceDeskId();

	const request = useCallback(
		(query: string) =>
			proformaGasAnalyticsFetch<UserPickerUser[]>(
				`${JSM_CUSTOMER_PORTAL_URL_PREFIX}/${serviceDeskId}/user-search/proforma?query=${encodeURIComponent(query)}`,
				{ method: 'GET' },
				createAnalyticsEvent,
				errorUtils,
			),
		[serviceDeskId, createAnalyticsEvent, errorUtils],
	);

	return useService(request);
};
